import { createContext, useContext, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { App, ContainerFeature, Plan, User } from '@back4app2/sdk';
import { toast } from 'react-hot-toast';
import back4app2 from '../back4app2';
import { DsButton, ModalDialog } from '../components/ds';
import { upgradePath } from '../containers/MyAppSettings';
import { cheapestPaidPlan, isFreePlanApp, planFeatures } from '../utils/plans';
import { planCheckoutSpec } from '../utils/planNames';
import { AmplitudeEvent, trackEvent } from '../utils/amplitude';
import LoadingSpinner from '../components/LoadingSpinner';
import useContainerPurchase from './useContainerPurchase';

/**
 * What every Upgrade button in the app shell does.
 *
 * Two answers, decided by the plan the app is on:
 *
 *   Free   the checkout opens right here, with the cheapest paid plan already
 *          chosen and nothing else in the basket. Someone on Free clicking
 *          Upgrade wants the permanent URL the button promised; a plan table
 *          with a CPU column between them and the card form is a question
 *          they did not ask.
 *   paid   the Plan section of Settings, where a change is priced against the
 *          subscription the app already has — picking a size IS the decision
 *          there.
 *
 * Only the owner buys. A collaborator's Upgrade goes to the Plan section,
 * which says that the plan is the owner's to change. The checkout used to
 * open for them too: it took their card, the server refused to apply a
 * payment to an app they do not own, and the screen said "Payment received".
 *
 * An entry point that needs a SIZE rather than just a paid plan says so
 * (`pick`) and lands on the Plan section even on Free — an app that ran out
 * of memory is not helped by the cheapest plan chosen for it.
 *
 * An entry point that sells one extra says which (`add: 'database'`). On Free
 * the checkout then opens with the cheapest paid plan that sells it and one
 * pack of it in the basket; everywhere else the Plan section opens with the
 * same extra seeded.
 *
 * The provider sits once in `MyApp`, so the shell renders ONE checkout modal
 * whichever button opened it. Entry points call `useUpgrade(appId)` and get a
 * function; outside the shell (no provider) it is the Settings link, which is
 * what every one of them did before.
 *
 * The purchase itself is `useContainerPurchase`, the same as the creation
 * wizard and the Plan section — `lazy`, because Settings runs the legacy plan
 * change's own Paddle on the same page, and Paddle keeps one event callback.
 */
interface UpgradeOptions {
  /** The kind of an extra to put in the basket. */
  add?: string;
  /** Open the plan table instead of buying the cheapest paid plan in place. */
  pick?: boolean;
  /** Which button this is, for the checkout's events. */
  source?: string;
}

const UpgradeContext = createContext<((appId: string, options?: UpgradeOptions) => void) | null>(null);

export const UpgradeProvider = ({ app, user, children }: {
  app: App,
  user?: User,
  children: React.ReactNode,
}) => {
  const navigate = useNavigate();
  const { purchase, checkoutModal } = useContainerPurchase({
    lazy: true,
    plainOverlay: true,
  });
  const [isWorking, setIsWorking] = useState(false);
  /** The plan that was just paid for, until the reader leaves for the Overview. */
  const [paidPlan, setPaidPlan] = useState<Plan | null>(null);

  const goToPlanSection = (appId: string, add?: string) => navigate(upgradePath(appId, add));

  const upgrade = async (appId: string, { add, pick, source = 'unknown' }: UpgradeOptions = {}) => {
    // Another app, no account to lock the checkout to, not the owner, a size
    // to choose, or not on Free: the Plan section answers every one of those.
    if (appId !== app.id || !user || !app.isOwner || pick || !isFreePlanApp(app)) {
      goToPlanSection(appId, add);

      return;
    }

    if (isWorking) {
      return;
    }

    setIsWorking(true);

    let plan: Plan | undefined;
    let feature: ContainerFeature | undefined;

    try {
      const [plans, features] = await Promise.all([
        back4app2.getPlans(),
        add ? back4app2.containerFeatures() : undefined,
      ]);

      feature = features?.find((candidate) => candidate.kind === add);
      // With an extra, the cheapest paid plan that SELLS it — the same pick
      // the Plan section makes for `?add=`.
      plan = cheapestPaidPlan(
        feature ? plans.filter((candidate) => feature!.planIds.includes(candidate.id)) : plans
      );
    } catch (e) {
      console.error('could not load the plans for the upgrade', e);
    }

    if (!plan || (add && !feature)) {
      // Nothing to pre-select — the Plan section loads the table with its
      // own error handling.
      setIsWorking(false);
      goToPlanSection(appId, add);

      return;
    }

    const eventData = { appId, source, planId: plan.id, price: plan.price, add };

    trackEvent(AmplitudeEvent.UPGRADE_CHECKOUT_STARTED, eventData);

    const outcome = await purchase({
      source: 'upgrade_button',
      appId,
      planId: plan.id,
      extras: feature ? [{ featureId: feature.id, quantity: 1 }] : [],
      user,
      summary: {
        eyebrow: `upgrade to ${plan.name}`,
        planName: plan.name,
        planSpec: planCheckoutSpec(plan),
        planPrice: plan.price,
        features: planFeatures(plan),
        lines: feature ? [{ id: feature.id, name: feature.name, price: feature.price }] : [],
      },
    });

    setIsWorking(false);

    if (outcome.ok && outcome.processing) {
      // A slow method (boleto, bank debit): nothing is paid yet, so there is
      // no "paid" dialog to show — the plan starts when the webhook says so.
      toast.success("Your payment is processing. The plan starts as soon as it clears — this can take a few business days.");
    } else if (outcome.ok) {
      trackEvent(AmplitudeEvent.UPGRADE_CHECKOUT_PAID, eventData);
      setPaidPlan(plan);
    } else if (outcome.reason === 'FAILED') {
      trackEvent(AmplitudeEvent.UPGRADE_CHECKOUT_FAILED, { ...eventData, message: outcome.message });
      toast.error(outcome.message || "We couldn't complete the payment.");
    } else {
      trackEvent(AmplitudeEvent.UPGRADE_CHECKOUT_CLOSED, eventData);
    }
  };

  // Where the Plan section sends a buyer too: the deploy the purchase started
  // can only be watched on the Overview, and the app shell is subscribed to
  // the app, so the new plan is already on the sidebar by then.
  const onPaid = () => {
    setPaidPlan(null);
    navigate(`/apps/${app.id}`);
  };

  return (
    <UpgradeContext.Provider value={upgrade}>
      {children}
      {/* Between the click and the checkout there are three requests — the
          plans, the price, the Paddle customer — and for as long as they took
          the screen showed nothing at all: a button that had been pressed and
          looked as if it had not. Same dim as the checkout that replaces it,
          so the one becomes the other without a flash. */}
      {isWorking && !checkoutModal ? (
        <div role="status" aria-live="polite" className="fixed inset-0 z-[9999] bg-black/70 flex items-center justify-center p-4">
          <div className="flex items-center gap-3 px-5 py-4 rounded-card border border-navy-400 bg-navy-800 shadow-[0_14px_48px_rgba(0,0,0,0.5)]">
            <LoadingSpinner width="1.25rem" height="1.25rem" />
            <span className="text-ds-135 text-white">Opening the checkout…</span>
          </div>
        </div>
      ) : null}
      {checkoutModal}
      <ModalDialog
        open={!!paidPlan}
        onClose={onPaid}
        title="Payment received"
        blurb="Your app is redeploying on the new plan. You can follow it on the Overview."
        footer={
          <DsButton variant="primary" onClick={onPaid}>
            Go to the Overview
          </DsButton>
        }
      />
    </UpgradeContext.Provider>
  );
};

/**
 * The one thing an Upgrade button calls. The options go on the hook, not on
 * the returned function: that one is handed straight to `onClick`, whose first
 * argument is the event.
 */
export default function useUpgrade(appId: string, options: UpgradeOptions = {}): () => void {
  const navigate = useNavigate();
  const upgrade = useContext(UpgradeContext);
  const { add, pick, source } = options;

  return () => (upgrade ? upgrade(appId, { add, pick, source }) : navigate(upgradePath(appId, add)));
}
